import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  Clock,
  Copy,
  Download,
  Edit,
  ExternalLink,
  FileText,
  Link2,
  Mail,
  MessageSquare,
  PauseCircle,
  QrCode,
  RefreshCw,
  Trash2,
  Wifi,
  WifiOff,
  XCircle,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import StatusBadge from "@/components/ui/StatusBadge";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import MandateChargesCard from "@/components/feature/wallet/MandateChargesCard";
import InstallmentSchedule from "@/pages/retailer/installments/components/InstallmentSchedule";
import ActivityTimeline, { type TimelineEntry } from "./components/ActivityTimeline";
import CancelModal from "./components/CancelModal";
import CustomerDocuments from "./components/CustomerDocuments";
import DeleteMandateModal from "./components/DeleteMandateModal";
import GenerateLinkModal from "./components/GenerateLinkModal";
import InfoCard from "./components/InfoCard";
import RocketPayResponseModal from "./components/RocketPayResponseModal";
import { formatDate, formatINR } from "./data";
import { mandateService, rocketpayService } from "@/services/apiServices";
import type { Mandate } from "@/types/mandate";
import { useMandateLiveUpdates, useRealtimeEvents } from "@/context/RealtimeEventsContext";

interface BannerConfig {
  icon: LucideIcon;
  bg: string;
  iconColor: string;
  titleColor: string;
  title: string;
  sub: string;
}

function receiptText(mandate: Mandate): string {
  const lines = [
    "MandatePro — Mandate Receipt",
    "----------------------------------------",
    `Reference No: ${mandate.refNo}`,
    `UMRN: ${mandate.umrn || "—"}`,
    `Status: ${mandate.status}`,
    `Customer: ${mandate.customerName} (${mandate.customerMobile})`,
    "----------------------------------------",
    `Product: ${[mandate.brand, mandate.model].filter(Boolean).join(" ") || "—"}`,
    `Product Price: ${formatINR(mandate.productPrice)}`,
    `Down Payment: ${formatINR(mandate.downPayment)}`,
    `Financed Amount: ${formatINR(mandate.financedAmount)}`,
    `Interest Rate: ${mandate.interestRate}% p.a.`,
    `Tenure: ${mandate.tenureMonths} months`,
    `Monthly EMI: ${formatINR(mandate.emiAmount)}`,
    `Total Payable: ${formatINR(mandate.totalPayable)}`,
    "----------------------------------------",
    `Auth Mode: ${mandate.authMode}`,
    mandate.authMode === "UPI Autopay" ? `UPI ID: ${mandate.upiId || "—"}` : `Bank: ${mandate.bankName || "—"}`,
    `Created On: ${formatDate(mandate.createdAt)}`,
    mandate.startDate ? `Start Date: ${mandate.startDate}` : "",
    mandate.endDate ? `End Date: ${mandate.endDate}` : "",
  ].filter(Boolean);
  return lines.join("\n");
}

export default function RetailerMandateDetail() {
  return (
    <ToastProvider>
      <DetailInner />
    </ToastProvider>
  );
}

function DetailInner() {
  const { refNo } = useParams<{ refNo: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isConnected, connectionStatus } = useRealtimeEvents();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mandate, setMandate] = useState<Mandate | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [syncingRocketPay, setSyncingRocketPay] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [generateLinkOpen, setGenerateLinkOpen] = useState(false);
  const [rpModalOpen, setRpModalOpen] = useState(false);
  const [rpLoading, setRpLoading] = useState(false);
  const [rpLiveObject, setRpLiveObject] = useState<any>(null);
  const [rpRefreshing, setRpRefreshing] = useState(false);
  const [rpCancelling, setRpCancelling] = useState(false);
  const [rpDeleting, setRpDeleting] = useState(false);

  function copyText(text: string, msg: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(msg))
      .catch(() => toast.error("Copy failed"));
  }

  useEffect(() => {
    if (!refNo) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    mandateService
      .getById(refNo, true)
      .then((res) => {
        if (cancelled) return;
        setMandate(res?.data?.mandate ?? null);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refNo]);

  // ─── [REAL-TIME SSE] Instant Push Listener ─────────────────────────────────
  // Primary update path: When a Webhook arrives from RocketPay (e.g. customer signs mandate),
  // the backend emits an SSE event over the persistent connection. This hook catches the event
  // instantly and updates the mandate state in-place without any polling or page reload.
  useMandateLiveUpdates((payload) => {
    // Re-check inside callback for extra safety (nested payload structure)
    if (refNo) {
      const payloadRef = payload?.refNo || payload?.mandate?.refNo;
      if (payloadRef && payloadRef !== refNo) return; // Mismatch — skip
    }

    if (payload?.mandate) {
      setMandate((prev) => (prev ? { ...prev, ...payload.mandate } : payload.mandate));
      setLastSyncedAt(new Date().toLocaleTimeString());
      toast.success(`Live Update: Mandate is now ${payload.mandate.status}`);
    } else if (payload?.status) {
      setMandate((prev) => (prev ? { ...prev, status: payload.status } : prev));
      setLastSyncedAt(new Date().toLocaleTimeString());
      toast.success(`Live Update: Mandate is now ${String(payload.status).replace(/_/g, " ")}`);
    }
  }, refNo);

  // ─── [FALLBACK POLLING 1] Offline Fallback Poller ───────────────────────────
  // Activates ONLY when SSE is disconnected (e.g. network blip or tab sleep).
  // Automatically paused as long as SSE (`isConnected === true`) is healthy.
  useEffect(() => {
    if (isConnected || !refNo) return;
    const interval = setInterval(() => {
      mandateService
        .getById(refNo)
        .then((res) => {
          const fresh = res?.data?.mandate;
          if (fresh) setMandate(fresh);
        })
        .catch(() => {/* silent */});
    }, 30_000);
    return () => clearInterval(interval);
  }, [isConnected, refNo]);

  // ─── [ACTIVE POLLING 2] Smart Pending-State Poller (?sync=true) ────────────
  // Purpose:
  // When a mandate is in PENDING_AUTH or CREATED (customer is currently authorizing on phone),
  // this poller calls `GET /api/mandates/:refNo?sync=true` every 15 seconds.
  // 
  // Why:
  // On localhost without public webhooks, RocketPay cannot push to your machine.
  // The `?sync=true` flag tells the backend to actively ping RocketPay and sync the DB.
  // 
  // Safety Controls:
  // - Pauses when browser tab is inactive (`document.hidden`).
  // - Stops automatically after 12 attempts (3 minutes) or as soon as status becomes ACTIVE/REJECTED.
  useEffect(() => {
    if (!mandate || !refNo) return;
    const PENDING_STATES = ["PENDING_AUTH", "CREATED", "AUTH PENDING", "INITIATED"];
    if (!PENDING_STATES.includes(mandate.status)) return;

    const MAX_POLLS = 12; // 12 × 15s = 3 minutes
    let pollCount = 0;

    const checkStatus = async () => {
      if (typeof document !== "undefined" && document.hidden) return false;
      try {
        const res = await mandateService.getById(refNo, true);
        const fresh = res?.data?.mandate;
        if (fresh && (!PENDING_STATES.includes(fresh.status) || fresh.status !== mandate.status)) {
          // Status changed! Update and stop polling if now active/completed.
          setMandate(fresh);
          setLastSyncedAt(new Date().toLocaleTimeString());
          if (!PENDING_STATES.includes(fresh.status)) {
            toast.success(`Mandate status updated to ${String(fresh.status).replace(/_/g, " ")}`);
            return true;
          }
        }
      } catch (_) { /* silent */ }
      return false;
    };

    const interval = setInterval(async () => {
      pollCount++;
      if (pollCount > MAX_POLLS) {
        clearInterval(interval);
        return;
      }
      const done = await checkStatus();
      if (done) clearInterval(interval);
    }, 15_000);

    return () => clearInterval(interval);
  }, [mandate?.status, refNo]);

  const timeline: TimelineEntry[] = useMemo(() => {
    if (!mandate) return [];
    const entries: TimelineEntry[] = [
      {
        id: "created",
        icon: FileText,
        title: "Mandate created",
        description: `Mandate generated for ${formatINR(mandate.emiAmount)}/month`,
        time: formatDate(mandate.createdAt),
      },
    ];
    if (mandate.status === "PENDING_AUTH" || mandate.status === "CREATED") {
      entries.push({
        id: "pending",
        icon: Clock,
        title: "Awaiting customer authorisation",
        description: "The customer has not yet completed the authorisation link.",
        time: formatDate(mandate.createdAt),
      });
    }
    if (mandate.status === "ACCEPTED") {
      entries.push({
        id: "accepted",
        icon: Clock,
        title: "Mandate accepted",
        description: "Customer completed authorisation, awaiting bank activation / UMRN",
        time: formatDate(mandate.createdAt),
        toneClass: "bg-blue-50 text-blue-700",
      });
    }
    if (mandate.status === "ACTIVE" || mandate.status === "ACTIVATED" || mandate.status === "COMPLETED") {
      entries.push({
        id: "active",
        icon: CheckCircle2,
        title: "Mandate authorised & active",
        description: mandate.umrn ? `Activated with UMRN ${mandate.umrn}` : "Customer completed authorisation",
        time: mandate.startDate ? formatDate(mandate.startDate) : formatDate(mandate.createdAt),
        toneClass: "bg-success-bg text-success",
      });
    }
    if (mandate.status === "COMPLETED") {
      entries.push({
        id: "completed",
        icon: CheckCircle2,
        title: "All installments completed",
        description: `${mandate.tenureMonths} of ${mandate.tenureMonths} installments collected`,
        time: mandate.endDate ? formatDate(mandate.endDate) : formatDate(mandate.createdAt),
        toneClass: "bg-success-bg text-success",
      });
    }
    if (mandate.status === "PAUSED") {
      entries.push({
        id: "paused",
        icon: PauseCircle,
        title: "Mandate paused",
        description: "Installment presentation is temporarily paused.",
        time: formatDate(mandate.createdAt),
        toneClass: "bg-warning-bg text-warning",
      });
    }
    if (mandate.status === "CANCELLED") {
      entries.push({
        id: "cancelled",
        icon: XCircle,
        title: "Mandate cancelled",
        description: mandate.cancelReason
          ? `Reason: ${mandate.cancelReason}${mandate.cancelRemarks ? ` — ${mandate.cancelRemarks}` : ""}`
          : "No further debits will be processed.",
        time: mandate.cancelledAt ? formatDate(mandate.cancelledAt) : formatDate(mandate.createdAt),
        toneClass: "bg-[#F1F5F9] text-slate-500",
      });
    }
    if (mandate.status === "HALTED") {
      entries.push({
        id: "halted",
        icon: XCircle,
        title: "Authorisation failed",
        description: "The customer's bank declined or failed to process the authorisation request.",
        time: formatDate(mandate.createdAt),
        toneClass: "bg-danger-bg text-danger",
      });
    }
    if (mandate.status === "TERMINATED") {
      entries.push({
        id: "terminated",
        icon: XCircle,
        title: "Mandate terminated",
        description: "The mandate was terminated by the bank or payment provider.",
        time: formatDate(mandate.createdAt),
        toneClass: "bg-danger-bg text-danger",
      });
    }
    return entries.reverse();
  }, [mandate]);

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6">
        <div className="flex flex-col gap-4">
          <div className="skeleton-shimmer h-8 w-40 rounded-md" />
          <div className="skeleton-shimmer h-24 rounded-[10px]" />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[62%_38%]">
            <div className="skeleton-shimmer h-[420px] rounded-[10px]" />
            <div className="skeleton-shimmer h-[420px] rounded-[10px]" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !mandate) {
    return (
      <div className="mx-auto w-full max-w-[900px] px-4 py-6 md:px-6">
        <EmptyState
          title="Mandate not found"
          description="This mandate reference could not be found."
          actionLabel="Back to Mandates"
          onAction={() => navigate("/retailer/mandates")}
        />
      </div>
    );
  }

  const isDraft = mandate.status === "DRAFT";
  const resendable = mandate.status === "PENDING_AUTH" || mandate.status === "CREATED";
  const cancelable = mandate.status === "ACTIVE" || mandate.status === "ACTIVATED";
  const downloadable =
    mandate.status === "ACTIVE" ||
    mandate.status === "ACTIVATED" ||
    mandate.status === "PAUSED" ||
    mandate.status === "COMPLETED";
  const deletable = mandate.status === "CREATED" || mandate.status === "DRAFT";

  const expiryD = new Date(mandate.createdAt);
  expiryD.setDate(expiryD.getDate() + 7);

  let banner: BannerConfig;
  if (mandate.status === "DRAFT") {
    banner = {
      icon: FileText,
      bg: "bg-amber-50 border-amber-200",
      iconColor: "text-amber-600",
      titleColor: "text-amber-900",
      title: "Draft Mandate · Link Not Generated",
      sub: "This mandate is saved as a draft. You can generate the authorisation link whenever you are ready or edit the details.",
    };
  } else if (mandate.status === "CREATED" || mandate.status === "PENDING_AUTH") {
    banner = {
      icon: Clock,
      bg: "bg-sky-50 border-sky-200",
      iconColor: "text-sky-600",
      titleColor: "text-sky-900",
      title: "Mandate Created · Awaiting Customer Authorisation",
      sub: `The authorisation link was sent on ${formatDate(mandate.createdAt)} and expires on ${formatDate(expiryD.toISOString())}.`,
    };
  } else if (mandate.status === "ACCEPTED") {
    banner = {
      icon: Clock,
      bg: "bg-blue-50 border-blue-200",
      iconColor: "text-blue-600",
      titleColor: "text-blue-900",
      title: "Mandate Accepted · Activation in Progress",
      sub: "Customer completed authorisation. Awaiting bank activation and UMRN generation.",
    };
  } else if (mandate.status === "ACTIVE" || mandate.status === "ACTIVATED") {
    banner = {
      icon: CheckCircle2,
      bg: "bg-success-bg border-emerald-200",
      iconColor: "text-success",
      titleColor: "text-emerald-900",
      title: "Mandate is active",
      sub: `UMRN ${mandate.umrn || "—"} · First debit on or after ${mandate.startDate || "—"}.`,
    };
  } else if (mandate.status === "PAUSED") {
    banner = {
      icon: PauseCircle,
      bg: "bg-warning-bg border-[#FDE68A]",
      iconColor: "text-warning",
      titleColor: "text-amber-900",
      title: "Mandate is paused",
      sub: "Installment presentation is temporarily stopped.",
    };
  } else if (mandate.status === "HALTED") {
    banner = {
      icon: XCircle,
      bg: "bg-danger-bg border-red-200",
      iconColor: "text-danger",
      titleColor: "text-red-900",
      title: "Mandate is halted",
      sub: "Stopped by the bank or the payment provider.",
    };
  } else if (mandate.status === "TERMINATED") {
    banner = {
      icon: XCircle,
      bg: "bg-danger-bg border-red-200",
      iconColor: "text-danger",
      titleColor: "text-red-900",
      title: "Mandate is terminated",
      sub: "The mandate was terminated by the bank or provider.",
    };
  } else if (mandate.status === "COMPLETED") {
    banner = {
      icon: CheckCircle2,
      bg: "bg-primary-50 border-sky-200",
      iconColor: "text-info",
      titleColor: "text-sky-900",
      title: "All installments completed",
      sub: `${mandate.tenureMonths} of ${mandate.tenureMonths} installments collected.`,
    };
  } else if (mandate.status === "CANCELLED") {
    banner = {
      icon: Ban,
      bg: "bg-[#F1F5F9] border-[#CBD5E1]",
      iconColor: "text-slate-500",
      titleColor: "text-slate-700",
      title: "Mandate cancelled",
      sub: mandate.cancelReason ? `Reason: ${mandate.cancelReason}` : "No further debits will be processed.",
    };
  } else {
    banner = {
      icon: Clock,
      bg: "bg-slate-100 border-slate-200",
      iconColor: "text-slate-500",
      titleColor: "text-slate-700",
      title: `Mandate Status: ${mandate.status || "Unknown"}`,
      sub: "Current mandate state as reported by the gateway.",
    };
  }

  const effectiveAuthUrl =
    mandate?.checkoutUrl ||
    (mandate?.authLink?.startsWith("http")
      ? mandate.authLink
      : `${window.location.origin}${mandate?.authLink || `/auth/${mandate?.refNo}`}`);

  function copyLink() {
    if (!mandate) return;
    navigator.clipboard
      .writeText(effectiveAuthUrl)
      .then(() => toast.success("Checkout link copied to clipboard"))
      .catch(() => toast.error("Could not copy auth link"));
  }

  function copyUmrn() {
    if (!mandate?.umrn) return;
    navigator.clipboard
      .writeText(mandate.umrn)
      .then(() => toast.success("UMRN copied to clipboard"))
      .catch(() => toast.error("Could not copy UMRN"));
  }

  async function refreshStatus() {
    if (!refNo) return;
    setRefreshing(true);
    try {
      if (mandate?.rocketpayMandateId) {
        try {
          // Check live status via GET /v4/mandates/<mandate_id>
          await rocketpayService.getMandateV4(mandate.rocketpayMandateId);
        } catch (rpErr: any) {
          console.warn("[RocketPay V4] Live status check notice:", rpErr?.message);
        }
      }
      const res = await mandateService.getById(refNo, true);
      const updated: Mandate | undefined = res?.data?.mandate;
      if (updated) {
        setMandate(updated);
        toast.info(`Status: ${updated.status.replace("_", " ")}`);
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to refresh status");
    } finally {
      setRefreshing(false);
    }
  }

  async function syncRocketPay() {
    const targetId = mandate?.id || mandate?.refNo || refNo;
    if (!targetId) return;
    setSyncingRocketPay(true);
    try {
      const res = await mandateService.syncRocketPay(targetId);
      if (res?.data?.mandate) {
        setMandate(res.data.mandate);
      } else {
        const refreshed = await mandateService.getById(refNo || targetId, true);
        if (refreshed?.data?.mandate) setMandate(refreshed.data.mandate);
      }
      setLastSyncedAt(new Date().toLocaleTimeString());
      toast.success("Mandate status synchronized successfully!");
    } catch (err: any) {
      toast.error(err?.message || "Failed to sync status");
    } finally {
      setSyncingRocketPay(false);
    }
  }

  async function viewRocketPayResponse() {
    setRpModalOpen(true);
    setRpLoading(true);
    try {
      const rpId = mandate?.rocketpayMandateId || mandate?.refNo;
      if (rpId) {
        const res = await rocketpayService.getMandateV4(rpId);
        const data = res?.data ?? res;
        setRpLiveObject(data);
        if (res?.data?.mandate || res?.data?.status || res?.data?.state) {
          // Re-fetch mandate if status updated
          const refreshed = await mandateService.getById(refNo || rpId);
          if (refreshed?.data?.mandate) setMandate(refreshed.data.mandate);
        }
      } else {
        setRpLiveObject(mandate?.rawResponse || null);
      }
    } catch (err: any) {
      setRpLiveObject(mandate?.rawResponse || { error: err?.message || "Failed to fetch live gateway response" });
    } finally {
      setRpLoading(false);
    }
  }

  async function triggerPostRefresh() {
    const rpId = mandate?.rocketpayMandateId || mandate?.refNo;
    if (!rpId) return;
    setRpRefreshing(true);
    try {
      const res = await rocketpayService.refreshMandateV4(rpId);
      const data = res?.data ?? res;
      setRpLiveObject(data);
      toast.success("Mandate status refreshed successfully!");
      const refreshed = await mandateService.getById(refNo || rpId);
      if (refreshed?.data?.mandate) setMandate(refreshed.data.mandate);
    } catch (err: any) {
      toast.error(err?.message || "Failed to refresh mandate");
    } finally {
      setRpRefreshing(false);
    }
  }

  async function triggerCancelMandate() {
    const rpId = mandate?.rocketpayMandateId || mandate?.refNo;
    if (!rpId) return;
    if (mandate?.status !== "ACTIVE" && mandate?.status !== "ACTIVATED") {
      toast.error("Cancel is only allowed when mandate is in ACTIVATED state");
      return;
    }
    if (!window.confirm("Are you sure you want to cancel this mandate on the gateway?")) return;
    setRpCancelling(true);
    try {
      const res = await rocketpayService.cancelMandateV4(rpId);
      const data = res?.data ?? res;
      setRpLiveObject(data);
      toast.success("Mandate cancelled successfully!");
      const refreshed = await mandateService.getById(refNo || rpId);
      if (refreshed?.data?.mandate) setMandate(refreshed.data.mandate);
    } catch (err: any) {
      toast.error(err?.message || "Failed to cancel mandate");
    } finally {
      setRpCancelling(false);
    }
  }

  async function triggerDeleteMandate() {
    const rpId = mandate?.rocketpayMandateId || mandate?.refNo;
    if (!rpId) return;
    if (mandate?.status !== "CREATED") {
      toast.error("Delete is only allowed when mandate is in CREATED state");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this mandate? (Allowed in CREATED state only)")) return;
    setRpDeleting(true);
    try {
      const res = await rocketpayService.deleteMandateV4(rpId);
      const data = res?.data ?? res;
      setRpLiveObject(data);
      toast.success("Mandate deleted successfully!");
      const refreshed = await mandateService.getById(refNo || rpId);
      if (refreshed?.data?.mandate) setMandate(refreshed.data.mandate);
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete mandate");
    } finally {
      setRpDeleting(false);
    }
  }

  function downloadReceipt() {
    if (!mandate) return;
    const blob = new Blob([receiptText(mandate)], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${mandate.refNo}-receipt.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Receipt downloaded");
  }

  function shareWhatsApp() {
    if (!mandate) return;
    const rawMobile = (mandate.customerMobile || "").replace(/\D/g, "");
    const formattedPhone = rawMobile.length === 10 ? `91${rawMobile}` : rawMobile;
    const productInfo = [mandate.brand, mandate.model].filter(Boolean).join(" ") || mandate.productDescription || "E-Mandate Facility";
    const shop = mandate.shopName || "Digi India Solutions";

    const netPayable = mandate.totalPayable || (mandate.emiAmount && mandate.tenureMonths ? Number(mandate.emiAmount) * Number(mandate.tenureMonths) : mandate.financedAmount);
    const message = `━━━━━━━━━━━━━━━━━━━━\n📄 *DIGI INDIA SOLUTIONS*\n*E-Mandate Authorization Request*\n━━━━━━━━━━━━━━━━━━━━\n\nDear *${mandate.customerName}*,\n\nGreetings from *${shop}*!\n\nYour digital auto-debit E-Mandate has been initiated. Please review the details below and complete the online authorization:\n\n📌 *Mandate Details:*\n• *Reference No:* \`${mandate.refNo}\`\n• *Item / Facility:* ${productInfo}\n• *Net Payable Amount:* ₹${Number(netPayable || 0).toLocaleString("en-IN")}\n• *Monthly EMI:* *₹${Number(mandate.emiAmount || 0).toLocaleString("en-IN")}*\n• *Tenure:* ${mandate.tenureMonths || "1"} Month(s)\n• *Payment Mode:* ${mandate.authMode || "UPI / eNACH"}\n\n👉 *Authorise Your Mandate Here:*\n${effectiveAuthUrl}\n\nℹ️ *Important Information:*\n• 100% paperless & secure bank authorization.\n• Please complete authorization within 24 hours.\n\n━━━━━━━━━━━━━━━━━━━━\n🏢 *Digi India Solutions*\n🌐 https://eman.digiindiasolutions.com\n📞 Support: support@digiindiasolutions.com\n━━━━━━━━━━━━━━━━━━━━`;

    const url = formattedPhone
      ? `https://api.whatsapp.com/send?phone=${formattedPhone}&text=${encodeURIComponent(message)}`
      : `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  function shareSms() {
    if (!mandate) return;
    const message = `Digi India Solutions: Dear ${mandate.customerName}, please authorise your E-Mandate of ₹${mandate.emiAmount}/mo (Ref: ${mandate.refNo}). Click to complete: ${effectiveAuthUrl}`;
    window.location.href = `sms:${mandate.customerMobile}?body=${encodeURIComponent(message)}`;
  }

  async function shareEmail() {
    if (!mandate) return;
    if (!mandate.customerEmail) {
      toast.error("No email address registered for this customer");
      return;
    }
    setSendingEmail(true);
    try {
      const res = await mandateService.sendAuthEmail(mandate.refNo);
      toast.success(res?.message || `Authorisation email sent to ${mandate.customerEmail}`);
    } catch (err: any) {
      const productInfo = [mandate.brand, mandate.model].filter(Boolean).join(" ") || mandate.productDescription || "E-Mandate Facility";
      const netPayable = mandate.totalPayable || (mandate.emiAmount && mandate.tenureMonths ? Number(mandate.emiAmount) * Number(mandate.tenureMonths) : mandate.financedAmount);
      const subject = `Action Required: Authorise Your E-Mandate [${mandate.refNo}] - Digi India Solutions`;
      const body = `Dear ${mandate.customerName},\n\nPlease authorise your E-Mandate for ${productInfo} (Monthly EMI: INR ${Number(mandate.emiAmount || 0).toLocaleString("en-IN")}, Net Payable: INR ${Number(netPayable || 0).toLocaleString("en-IN")}).\n\nAuthorisation Link: ${effectiveAuthUrl}\n\nThank you,\nDigi India Solutions`;
      window.open(`mailto:${mandate.customerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
    } finally {
      setSendingEmail(false);
    }
  }

  async function confirmCancel(reason: string, remarks: string) {
    if (!mandate) return;
    if (mandate.status !== "ACTIVE" && mandate.status !== "ACTIVATED") {
      toast.error("Cancel is only allowed when mandate is in ACTIVATED state");
      return;
    }
    setCancelLoading(true);
    try {
      const res = await mandateService.cancel(mandate.id, reason, remarks);
      setMandate(res?.data?.mandate ?? null);
      setCancelOpen(false);
      toast.success(res?.message || "Mandate cancelled successfully");
    } catch (err: any) {
      toast.error(err?.message || "Failed to cancel mandate");
    } finally {
      setCancelLoading(false);
    }
  }

  async function confirmDelete() {
    if (!mandate) return;
    if (mandate.status !== "CREATED" && mandate.status !== "DRAFT") {
      toast.error("Delete is only allowed when mandate is in CREATED or DRAFT state");
      return;
    }
    setDeleteLoading(true);
    try {
      const res = await mandateService.delete(mandate.id);
      toast.success(res?.message || "Mandate deleted");
      navigate("/retailer/mandates");
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete mandate");
    } finally {
      setDeleteLoading(false);
      setDeleteOpen(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6">
      <button
        type="button"
        onClick={() => navigate("/retailer/mandates")}
        className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-ink-secondary hover:text-ink cursor-pointer"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Mandates
      </button>

      <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold text-ink">{mandate.refNo}</h1>
            <StatusBadge status={mandate.status} />
            {/* Real-time connection status badge */}
            <div
              title={connectionStatus === "connected" ? "Live updates active — status changes automatically" : "Offline — auto-polling every 30s"}
              className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                connectionStatus === "connected"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : connectionStatus === "connecting"
                  ? "border-amber-200 bg-amber-50 text-amber-700"
                  : "border-slate-200 bg-slate-50 text-slate-400"
              }`}
            >
              {connectionStatus === "connected" ? <Wifi className="h-2.5 w-2.5" /> : <WifiOff className="h-2.5 w-2.5" />}
              {connectionStatus === "connected" ? "Live" : connectionStatus === "connecting" ? "Connecting" : "Offline"}
            </div>
          </div>
          <p className="mt-1 text-[13px] text-ink-secondary">Created on {formatDate(mandate.createdAt)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {isDraft && (
            <>
              <Button
                variant="primary"
                leftIcon={<Zap className="h-4 w-4" />}
                onClick={() => setGenerateLinkOpen(true)}
              >
                Generate Mandate Link
              </Button>
              <Button
                variant="secondary"
                leftIcon={<Edit className="h-4 w-4" />}
                onClick={() => navigate(`/retailer/create-mandate?draftId=${mandate.id}`)}
              >
                Edit Draft
              </Button>
            </>
          )}
          {/* {resendable && (
            <Button variant="secondary" leftIcon={<Link2 className="h-4 w-4" />} onClick={copyLink}>
              Copy Link
            </Button>
          )} */}
          {/* {resendable && (
            <Button
              variant="secondary"
              leftIcon={<ExternalLink className="h-4 w-4" />}
              onClick={() => window.open(mandate.checkoutUrl || mandate.authLink, "_blank", "noopener,noreferrer")}
            >
              Preview Link
            </Button>
          )} */}
          {downloadable && (
            <Button variant="secondary" leftIcon={<Download className="h-4 w-4" />} onClick={downloadReceipt}>
              Download Receipt
            </Button>
          )}
          {cancelable && (
            <Button
              variant="secondary"
              className="text-danger hover:bg-danger-bg"
              leftIcon={<Ban className="h-4 w-4" />}
              onClick={() => setCancelOpen(true)}
            >
              Cancel Mandate
            </Button>
          )}
          {deletable && (
            <Button
              variant="secondary"
              className="text-danger hover:bg-danger-bg"
              leftIcon={<Trash2 className="h-4 w-4" />}
              onClick={() => setDeleteOpen(true)}
            >
              {isDraft ? "Delete Draft" : "Delete Mandate"}
            </Button>
          )}
          {/* <Button
            variant="secondary"
            leftIcon={<FileText className="h-4 w-4 text-sky-600" />}
            onClick={viewRocketPayResponse}
          >
            Gateway Response
          </Button> */}
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />}
            onClick={refreshStatus}
          >
            Refresh Status
          </Button>
        </div>
      </div>

      <div className={`mt-6 flex items-start gap-3 rounded-[10px] border p-4 ${banner.bg}`}>
        <banner.icon className={`mt-0.5 h-5 w-5 shrink-0 ${banner.iconColor}`} />
        <div>
          <p className={`text-sm font-semibold ${banner.titleColor}`}>{banner.title}</p>
          <p className="mt-0.5 text-[13px] text-ink-secondary">{banner.sub}</p>
        </div>
      </div>

      {mandate.rocketpayMandateId && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-sky-200 bg-sky-50/70 px-4 py-3 text-xs text-sky-900">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sky-800">Gateway Mandate ID:</span>
            <code className="rounded bg-white px-2 py-0.5 font-mono text-sky-950 border border-sky-200">
              {mandate.rocketpayMandateId}
            </code>
          </div>
          {/* <button
            type="button"
            onClick={viewRocketPayResponse}
            className="inline-flex items-center gap-1 font-semibold text-sky-700 hover:text-sky-900 hover:underline cursor-pointer"
          >
            Click here to see gateway response object &rarr;
          </button> */}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[62%_38%]">
        <div className="flex flex-col gap-4">
          <InfoCard
            title="Customer Details"
            collapsible
            defaultOpen={false}
            rows={[
              { label: "Customer Name", value: mandate.customerName },
              { label: "Mobile Number", value: mandate.customerMobile },
              { label: "Email Address", value: mandate.customerEmail || "—" },
              { label: "Date of Birth", value: mandate.customerDob ? formatDate(mandate.customerDob) : "—" },
              { label: "PAN", value: mandate.customerPan ?? "—" },
              { label: "Aadhaar", value: <code className="font-mono text-[13px] text-ink">{mandate.customerAadhaar ?? "—"}</code> },
              { label: "Address", value: mandate.customerAddress ?? "—" },
              {
                label: "City / State / Pincode",
                value:
                  [mandate.customerCity, mandate.customerState, mandate.customerPincode].filter(Boolean).join(", ") ||
                  "—",
              },
            ]}
          />

          <InfoCard
            title="Product Details"
            collapsible
            defaultOpen={false}
            rows={[
              { label: "Product Category", value: mandate.category ?? "—" },
              { label: "Brand", value: mandate.brand ?? "—" },
              { label: "Model", value: mandate.model ?? "—" },
              {
                label: "Serial Number or IMEI",
                value: <code className="font-mono text-[13px] text-ink">{mandate.serialNumber ?? "—"}</code>,
              },
              { label: "Invoice Number", value: mandate.invoiceNumber ?? "—" },
              { label: "Invoice Date", value: mandate.invoiceDate ? formatDate(mandate.invoiceDate) : "—" },
              { label: "Description", value: mandate.productDescription ?? "—" },
            ]}
          />

          <InfoCard title="EMI Details" collapsible defaultOpen={false}>
            <dl className="mt-2 flex flex-col">
              {[
                { label: "Product Price", value: formatINR(mandate.productPrice) },
                { label: "Down Payment", value: formatINR(mandate.downPayment) },
                { label: "Financed Amount", value: formatINR(mandate.financedAmount) },
                { label: "Interest Rate", value: `${mandate.interestRate}% p.a.` },
                { label: "Tenure", value: `${mandate.tenureMonths} months` },
                { label: "Total Interest", value: formatINR(mandate.totalInterest) },
                { label: "Total Payable", value: formatINR(mandate.totalPayable) },
              ].map((row, i) => (
                <div
                  key={row.label}
                  className={`flex items-center justify-between gap-4 py-2.5 ${i > 0 ? "border-t border-[#F1F5F9]" : ""}`}
                >
                  <dt className="shrink-0 text-[13px] text-ink-secondary">{row.label}</dt>
                  <dd className="text-right text-sm font-medium text-ink">{row.value}</dd>
                </div>
              ))}
              <div className="my-2 h-[3px] rounded bg-slate-200" />
              <div className="flex items-center justify-between gap-4 py-1.5">
                <span className="text-sm font-semibold text-ink">Monthly EMI</span>
                <span className="text-base font-bold text-primary-500">{formatINR(mandate.emiAmount)}</span>
              </div>
            </dl>
          </InfoCard>

          {mandate.authMode === "UPI Autopay" ? (
            <InfoCard title="UPI Details" rows={[{ label: "UPI ID", value: mandate.upiId ?? "—" }]} />
          ) : (
            <InfoCard
              title="Bank Details"
              rows={[
                { label: "Bank Name", value: mandate.bankName ?? "—" },
                { label: "Account Type", value: `${mandate.accountType || "Savings"} Account` },
                ...(mandate.accountHolderName ? [{ label: "Account Holder", value: mandate.accountHolderName }] : []),
                {
                  label: "Account Number",
                  value: <code className="font-mono text-[13px] text-ink">{mandate.accountNumber ?? "—"}</code>,
                },
                { label: "IFSC Code", value: mandate.ifscCode ?? "—" },
              ]}
            />
          )}

          <InfoCard
            title="Mandate Details"
            rows={[
              { label: "Authentication Mode", value: mandate.authMode },
              { label: "Start Date", value: mandate.startDate ? formatDate(mandate.startDate) : "—" },
              { label: "End Date", value: mandate.endDate ? formatDate(mandate.endDate) : "—" },
              ...(mandate.frequency ? [{ label: "Frequency", value: mandate.frequency }] : []),
              ...(mandate.purpose ? [{ label: "Purpose", value: mandate.purpose }] : []),
              ...(mandate.remarks ? [{ label: "Remarks", value: mandate.remarks }] : []),
              ...(mandate.umrn
                ? [
                    {
                      label: "UMRN",
                      value: (
                        <span className="inline-flex items-center justify-end gap-2">
                          <code className="font-mono text-[13px] text-ink">{mandate.umrn}</code>
                          <button
                            type="button"
                            onClick={copyUmrn}
                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 cursor-pointer"
                            aria-label="Copy UMRN"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
          />

          {(mandate.rocketpayMandateId || mandate.checkoutUrl) && (
            <div className="rounded-[10px] border border-primary/30 bg-primary-50/40 p-5 shadow-card">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
                  <span>Gateway Mandate Details</span>
                  <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                    Live Synced
                  </span>
                </h3>
              </div>
              <dl className="mt-4 flex flex-col gap-2.5">
                {mandate.rocketpayMandateId && (
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-[13px] text-ink-secondary">Gateway Mandate ID</dt>
                    <dd className="font-mono text-xs text-ink font-semibold">{mandate.rocketpayMandateId}</dd>
                  </div>
                )}
                {mandate.checkoutUrl && (
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-[13px] text-ink-secondary">Gateway Checkout URL</dt>
                    <dd>
                      <a
                        href={mandate.checkoutUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-primary font-medium hover:underline inline-flex items-center gap-1"
                      >
                        Open Checkout <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </dd>
                  </div>
                )}
                {mandate.qrCode && (
                  <div className="mt-1 border-t border-line/60 pt-2 flex flex-col gap-1">
                    <span className="text-xs font-medium text-ink-secondary">UPI AutoPay QR Intent</span>
                    <input
                      readOnly
                      value={mandate.qrCode}
                      className="w-full bg-white border border-line rounded px-2 py-1 text-[11px] font-mono text-ink-secondary"
                    />
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          {/* Gateway Integration Card */}
          {/* {(mandate.rocketpayMandateId || mandate.checkoutUrl || mandate.paymentOrderId) && (
            <div className="rounded-[10px] border border-primary-100 bg-gradient-to-br from-primary-50/40 via-white to-sky-50/30 p-5 shadow-card">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-ink flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary-600" />
                  Gateway Integration
                </h3>
                <div className="flex items-center gap-2">
                  <StatusBadge status={mandate.rawRocketPayResponse?.state || mandate.status} size="sm" />
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-3 text-xs">
                {mandate.rocketpayMandateId && (
                  <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
                    <span className="text-ink-secondary">Mandate / MMS ID</span>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono font-semibold text-primary-700 bg-white px-2 py-0.5 rounded border border-primary-200">
                        {mandate.rocketpayMandateId}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyText(mandate.rocketpayMandateId!, "Gateway Mandate ID copied")}
                        className="p-1 hover:bg-slate-100 rounded text-ink-muted hover:text-ink cursor-pointer"
                        title="Copy Mandate ID"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}

                {mandate.paymentOrderId && (
                  <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
                    <span className="text-ink-secondary">Payment Order ID</span>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-slate-700 bg-white px-2 py-0.5 rounded border border-slate-200">
                        {mandate.paymentOrderId}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyText(mandate.paymentOrderId!, "Payment Order ID copied")}
                        className="p-1 hover:bg-slate-100 rounded text-ink-muted hover:text-ink cursor-pointer"
                        title="Copy Order ID"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}

                {mandate.qrUrl && (
                  <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
                    <span className="text-ink-secondary">Gateway Simulator QR</span>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="h-7 text-xs border-primary-200 bg-white text-primary-600 hover:bg-primary-50"
                      leftIcon={<QrCode className="h-3.5 w-3.5" />}
                      onClick={() => window.open(mandate.qrUrl!, "_blank", "noopener,noreferrer")}
                    >
                      Open QR Gateway
                    </Button>
                  </div>
                )}

                {lastSyncedAt && (
                  <div className="flex items-center justify-between text-[11px] text-ink-muted pt-1">
                    <span>Last Synced with Gateway</span>
                    <span className="font-medium text-ink-secondary">{lastSyncedAt}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-primary-100 flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1 text-xs"
                  loading={syncingRocketPay}
                  leftIcon={<RefreshCw className={`h-3.5 w-3.5 ${syncingRocketPay ? "animate-spin" : ""}`} />}
                  onClick={syncRocketPay}
                >
                  Sync & Refresh
                </Button>

                {effectiveAuthUrl.startsWith("http") && (
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-1 text-xs"
                    leftIcon={<ExternalLink className="h-3.5 w-3.5" />}
                    onClick={() => window.open(effectiveAuthUrl, "_blank", "noopener,noreferrer")}
                  >
                    Open Checkout
                  </Button>
                )}

                {mandate.rawRocketPayResponse && (
                  <button
                    type="button"
                    onClick={() => setShowRawJson(!showRawJson)}
                    className="w-full text-center text-[11px] text-primary-600 hover:underline pt-1 cursor-pointer"
                  >
                    {showRawJson ? "Hide Raw Gateway Response" : "View Raw Gateway Response (JSON)"}
                  </button>
                )}
              </div>

              {showRawJson && mandate.rawRocketPayResponse && (
                <div className="mt-3 overflow-hidden rounded-md border border-slate-200 bg-slate-900 p-3 text-[11px] font-mono text-emerald-400 max-h-56 overflow-y-auto">
                  <pre>{JSON.stringify(mandate.rawRocketPayResponse, null, 2)}</pre>
                </div>
              )}
            </div>
          )} */}

          {resendable && (
            <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
              <h3 className="text-lg font-semibold text-ink">Authorisation Link</h3>
              <div className="mt-4">
                <span className="mb-1.5 block text-[13px] font-medium text-ink-secondary">Link</span>
                <div className="flex overflow-hidden rounded-lg border border-slate-300">
                  <input
                    readOnly
                    value={effectiveAuthUrl}
                    className="h-[42px] w-full bg-page px-3 text-[13px] font-mono text-ink outline-none truncate"
                    aria-label="Authorisation link"
                  />
                  <button
                    type="button"
                    onClick={copyLink}
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-l border-slate-300 bg-white px-3 text-sm font-medium text-ink hover:bg-slate-50 cursor-pointer"
                  >
                    Copy
                  </button>
                </div>
                <p className="mt-2 text-xs text-ink-secondary">Expires on {formatDate(expiryD.toISOString())}</p>
              </div>
              <div className="mt-4 flex flex-col gap-2.5">
                <Button variant="secondary" fullWidth leftIcon={<MessageSquare className="h-4 w-4" />} onClick={shareWhatsApp}>
                  Share on WhatsApp
                </Button>
                <Button variant="secondary" fullWidth leftIcon={<ExternalLink className="h-4 w-4" />} onClick={shareSms}>
                  Send SMS
                </Button>
                <Button variant="secondary" fullWidth loading={sendingEmail} leftIcon={<Mail className="h-4 w-4" />} onClick={shareEmail}>
                  Send Email
                </Button>
              </div>
            </div>
          )}

          <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
            <h3 className="text-lg font-semibold text-ink">Activity Timeline</h3>
            <div className="mt-4">
              <ActivityTimeline entries={timeline} />
            </div>
          </div>

          <CustomerDocuments mandate={mandate} />

          <MandateChargesCard
            status={mandate.status}
            refNo={mandate.refNo}
            ledgerLink={
              <button
                type="button"
                onClick={() => navigate(`/retailer/wallet?ref=${encodeURIComponent(mandate.refNo)}`)}
                className="text-xs font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
              >
                View in wallet ledger
              </button>
            }
          />
        </div>
      </div>

      {/* Installments remain backed by the separate (still-mocked) installments module;
          only `refNo` is used across that boundary. */}
      {(mandate.status === "ACTIVE" || mandate.status === "ACTIVATED") && <InstallmentSchedule mandate={mandate as any} />}

      <CancelModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        refNo={mandate.refNo}
        onConfirm={confirmCancel}
        loading={cancelLoading}
      />

      <DeleteMandateModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        refNo={mandate.refNo}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        isDraft={mandate.status === "DRAFT"}
      />

      <GenerateLinkModal
        open={generateLinkOpen}
        onClose={() => setGenerateLinkOpen(false)}
        mandate={mandate}
        onSuccess={(updated) => {
          if (updated) {
            setMandate(updated);
          } else if (refNo) {
            mandateService.getById(refNo, true).then((r) => {
              if (r?.data?.mandate) setMandate(r.data.mandate);
            });
          }
          setGenerateLinkOpen(false);
          toast.success("Mandate link generated successfully!");
        }}
      />

      <RocketPayResponseModal
        open={rpModalOpen}
        onClose={() => setRpModalOpen(false)}
        mandateId={mandate.rocketpayMandateId || mandate.refNo}
        responseObject={rpLiveObject}
        loading={rpLoading}
        onRefresh={viewRocketPayResponse}
        onTriggerPostRefresh={triggerPostRefresh}
        refreshingPost={rpRefreshing}
        onTriggerCancel={triggerCancelMandate}
        cancelling={rpCancelling}
        onTriggerDelete={triggerDeleteMandate}
        deleting={rpDeleting}
      />
    </div>
  );
}
